import { useState } from "react";
import { Button } from "@/components/ui/button";
import API_URL from "@/config";

interface PropertyGalleryProps {
  images: string[];
  title: string;
  videos : string[]
}

export default function PropertyGallery({ images, title, videos }: PropertyGalleryProps) {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Fallback to placeholder if no images
  const galleryImages = (images?.length > 0 ? images.map((image) =>({ type : "image", src : `${API_URL}/getImage/image/${image}`})) : []).concat(
        videos?.map((vid) => ({ type: "video", src: `${API_URL}/getImage/video/${vid}` })) || []
  );

  const fallback = [
    { type: "image", src: "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?auto=format&fit=crop&w=1200&h=800" }
  ];

  const media = galleryImages.length > 0 ? galleryImages : fallback;

  const nextImage = () => {
    setCurrentIndex((prev) => (prev + 1) % galleryImages.length);
  };

  const previousImage = () => {
    setCurrentIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  const select = (index: number) => setCurrentIndex(index);

  return (
    <div className="mb-6">
      {/* Main Image */}
      <div className="relative">
        {media[currentIndex].type === "image" ? (
          <img
            src={media[currentIndex].src}
            alt={`${title} - Image ${currentIndex + 1}`}
            className="w-full h-96 object-cover rounded-lg"
          />
        ) : (
          <video
            src={media[currentIndex].src}
            controls
            className="w-full h-96 object-cover rounded-lg"
          />
        )}
        
        {galleryImages.length > 1 && (
          <>
            {/* Navigation Arrows */}
            <button
              onClick={previousImage}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full p-2 shadow-md transition-all"
              data-testid="button-gallery-previous"
            >
              <i className="fas fa-chevron-left text-gray-700"></i>
            </button>
            
            <button
              onClick={nextImage}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full p-2 shadow-md transition-all"
              data-testid="button-gallery-next"
            >
              <i className="fas fa-chevron-right text-gray-700"></i>
            </button>
          </>
        )}
        
        {/* Image Counter */}
        <div className="absolute top-4 right-4 bg-white px-3 py-2 rounded-lg shadow-md text-sm font-medium" data-testid="text-gallery-counter">
          <i className="fas fa-images mr-1"></i>
          {currentIndex + 1} / {galleryImages.length}
        </div>
      </div>
      
      {/* Thumbnail Gallery */}
      {media.length > 1 && (
        <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2 mt-4">
          {media.slice(0, 8).map((item, index) => (
            <button
              key={index}
              onClick={() => select(index)}
              className={`w-full h-20 rounded cursor-pointer transition-all ${
                index === currentIndex
                  ? "border-2 border-primary opacity-100"
                  : "border border-gray-200 opacity-75 hover:opacity-100"
              }`}
            >
              {item.type === "image" ? (
                <img
                  src={item.src}
                  alt={`${title} - Thumbnail ${index + 1}`}
                  className="w-full h-full object-cover rounded"
                />
              ) : (
                <video
                  src={item.src}
                  className="w-full h-full object-cover rounded"
                />
              )}
            </button>
          ))}

          {media.length > 8 && (
            <div className="w-full h-20 bg-gray-100 rounded flex items-center justify-center text-gray-500 text-sm">
              <div className="text-center">
                <i className="fas fa-plus mb-1"></i>
                <div>+{media.length - 8}</div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
